import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from '@/components';
import { sitePhotosApi, type SitePhotoInput, type SitePhotoPost } from '@/lib/apiClient';
import { uploadToJobFolder, GraphConsentError } from '@/lib/graphUpload';
import { isDefiniteRejection, isConflictError } from '@/lib/httpErrors';
// Static, not `await import()`: the lazy chunk could fail to load on a
// dropped connection mid-post, outside every try, leaving the sheet stuck with
// `posting` on and Cancel disabled (review of #560). It is a few hundred
// bytes; exifr, the big one, is still lazy and its loader already fails safe.
import { compressImageSized, uploadExtension } from '@/lib/compressImage';
import { randomId } from '@/lib/randomId';
import {
  readPhotoMeta, getDeviceLocation, deviceFixApplies, distanceMeters, describeDistance,
  localYmd, FAR_FROM_SITE_METERS, type PhotoMeta, type DeviceFix,
} from '@/lib/photoMeta';

/**
 * The "new post" sheet: preview the picked photos, read when and where each
 * was taken, add a caption, pick the site day, then upload and post.
 *
 * Metadata is read from the ORIGINAL files here, before compression strips it.
 * When a photo carries no GPS and was taken within the last hour (or we can't
 * tell when), the phone's current position stands in, labeled as such.
 *
 * Uploads are remembered per file for as long as the sheet is open. A retry —
 * after a dropped connection mid-batch, or the final post failing — uploads
 * only what hasn't gone up yet, so the Job Folder doesn't collect duplicates.
 */

export const SUBFOLDER = 'Daily Log Photos';

/** Mirrors MAX_PHOTOS_PER_POST in the API DTO. Enforced HERE, before any
 *  upload — the API refusing a 31st photo after the upload would leave every
 *  file in the Job Folder with nothing pointing at it. */
export const MAX_PHOTOS = 30;

/**
 * Longest edge a site photo is stored at. Site photos are looked at zoomed in,
 * to find a condition months later, so they keep more detail than the
 * 1920 px every other upload uses: at 1920 a zoomed crack went soft (#665
 * follow-up, product owner chose ~3000 on 2026-10-20). Bigger files and a
 * slower post on a weak site connection are the price. A phone photo smaller
 * than this is never scaled up.
 */
export const SITE_PHOTO_MAX_DIM = 3000;

interface Picked {
  file: File;
  url: string;
  meta: PhotoMeta | null; // null while reading
}

const css = `
  .spc-body { display: grid; gap: var(--ew-s-4); }
  .spc-grid { display: grid; gap: var(--ew-s-2); grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); }
  .spc-tile { display: grid; gap: 4px; }
  .spc-thumb {
    position: relative; aspect-ratio: 1 / 1; border-radius: var(--ew-r-md); overflow: hidden;
    background: var(--ew-n-100); border: 1px solid var(--ew-n-100);
  }
  .spc-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .spc-remove {
    position: absolute; top: 4px; right: 4px; width: 24px; height: 24px; border-radius: 50%;
    background: rgba(0, 0, 0, 0.6); color: #fff; border: 0; cursor: pointer; font-size: 15px; line-height: 1;
  }
  .spc-meta { font: var(--ew-fw-regular) 11px/1.3 var(--ew-font); color: var(--ew-n-500); }
  .spc-field { display: grid; gap: 5px; }
  .spc-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .spc-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 9px 10px; width: 100%;
    font: var(--ew-fw-regular) var(--ew-fs-body-ui) var(--ew-font); color: var(--ew-dark); background: #fff;
    box-sizing: border-box;
  }
  .spc-note { font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font); color: var(--ew-n-500); margin: 0; }
  .spc-warn {
    padding: 8px 12px; border-radius: var(--ew-r-md); background: var(--ew-safety-50);
    color: var(--ew-safety-700); border: 1px solid var(--ew-safety-100);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
  .spc-error {
    padding: 8px 12px; background: #FBE4DF; color: #8F2414; border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
`;

/** Any failure to get a SharePoint token. It used to always say "admin
 *  consent pending", which is only one of the reasons (review of #560). */
const SIGN_IN_FAILED =
  'Couldn’t sign in to SharePoint. The sign-in window may have been blocked or closed — allow pop-ups for this site and try again. If it keeps happening, SharePoint access may still be being set up for your account.';

/** An error's message as a sentence, so our follow-on text doesn't run into it. */
function sentence(e: unknown, fallback: string): string {
  const msg = (e instanceof Error ? e.message : '').trim() || fallback;
  return /[.!?]$/.test(msg) ? msg : `${msg}.`;
}

function fmtClock(d: Date): string {
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function SitePhotoComposer({
  projectId,
  jobNumber,
  jobFolderUrl,
  files,
  fixedDate,
  site,
  getToken,
  onClose,
  onPosted,
}: {
  projectId: number;
  jobNumber: string;
  jobFolderUrl: string;
  files: File[];
  /** When opened from a daily log, the post belongs to that log's day. */
  fixedDate?: string;
  site: { latitude: number; longitude: number } | null;
  getToken: () => Promise<string>;
  onClose: () => void;
  onPosted: (post: SitePhotoPost) => void;
}) {
  const [picked, setPicked] = useState<Picked[]>(() =>
    files.slice(0, MAX_PHOTOS).map((file) => ({ file, url: URL.createObjectURL(file), meta: null })));
  const leftOut = Math.max(0, files.length - MAX_PHOTOS);
  /** Files already in the Job Folder → what the API needs to know about them. */
  const uploadedRef = useRef(new Map<File, SitePhotoInput>());
  /**
   * Each file's SharePoint name, fixed the first time it is sent. An upload
   * can land in SharePoint and still fail here (the reply was lost), so a
   * retry must reuse the name and REPLACE — a fresh name, or 'rename', would
   * leave the first copy orphaned in the Job Folder (review of #560). The
   * name carries the job, day, first-attempt time, index and a random tag, so
   * the only file it can ever replace is this file's own earlier copy.
   */
  const nameRef = useRef(new Map<File, string>());
  /** One id for this batch, sent on every attempt to post it. If an attempt
   *  was saved but its reply was lost, the retry gets that post back instead
   *  of making a second one (review of #560). */
  const requestIdRef = useRef('');
  if (!requestIdRef.current) requestIdRef.current = randomId();
  /**
   * What the FIRST post attempt sent, frozen from then on. Every later
   * attempt resends exactly this — same photos, caption and day — because the
   * server answers a repeated request id with whatever the first attempt
   * saved. Letting a retry add photos uploaded afterwards would get the
   * earlier, partial post back and silently drop the additions (review of
   * #560). Photos left out of a frozen attempt go in a new post.
   */
  const attemptRef = useRef<{ photoDate: string; caption?: string; photos: SitePhotoInput[] } | null>(null);
  const [frozen, setFrozen] = useState(false);
  /** The API said this can never go through (a 409: the job was locked, or
   *  this batch's post was removed). Only Cancel is left (review of #560). */
  const [finalError, setFinalError] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [caption, setCaption] = useState('');
  const [photoDate, setPhotoDate] = useState<string>(fixedDate ?? localYmd(new Date()));
  const [dateTouched, setDateTouched] = useState(!!fixedDate);
  const [device, setDevice] = useState<DeviceFix | null>(null);
  const [locating, setLocating] = useState(false);
  /** The one phone-location attempt has finished (with a fix or without). */
  const [locationDone, setLocationDone] = useState(false);
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Revoke preview URLs on unmount.
  useEffect(() => () => { picked.forEach((p) => URL.revokeObjectURL(p.url)); }, []); // mount/unmount only

  // Read each file's capture time + GPS.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const p of picked) {
        const meta = await readPhotoMeta(p.file);
        if (cancelled) return;
        setPicked((prev) => prev.map((x) => (x.file === p.file ? { ...x, meta } : x)));
      }
    })();
    return () => { cancelled = true; };
  }, []); // mount/unmount only

  const allRead = picked.every((p) => p.meta !== null);
  /** Some recent photo came without GPS, so the phone's position is wanted. */
  const needsDevice = picked.some((p) => p.meta && p.meta.latitude == null && deviceFixApplies(p.meta.takenAt));
  // Post waits for that attempt. It is computed here, in render, rather than
  // read from `locating` alone: the effect below only sets `locating` AFTER
  // the render in which the last file finishes reading, and a tap in that gap
  // would post without the location (review of #560). getDeviceLocation gives
  // up after ~11s, so this can never hold Post forever.
  const waitingForLocation = allRead && needsDevice && !locationDone;

  // Once everything is read: default the site day to the earliest capture day
  // (so last night's photos land on yesterday), and ask the phone where it is
  // if any recent photo came without GPS.
  useEffect(() => {
    if (!allRead) return;
    if (!dateTouched) {
      // The earliest photo's day AS THE CAMERA WROTE IT — not its instant
      // converted to this phone's zone, which can land a late-evening photo
      // uploaded from another zone on the wrong day's log (review of #560).
      const dated = picked.map((p) => p.meta).filter((m): m is PhotoMeta => !!m?.takenAt);
      if (dated.length) {
        const first = dated.reduce((a, b) => (b.takenAt!.getTime() < a.takenAt!.getTime() ? b : a));
        setPhotoDate(first.takenYmd ?? localYmd(first.takenAt!));
      }
    }
    if (needsDevice && !device && !locating) {
      setLocating(true);
      getDeviceLocation().then((fix) => { setDevice(fix); setLocating(false); setLocationDone(true); });
    }
  }, [allRead]); // once, when the last file has been read

  /** The location each photo will be saved with. */
  const located = useMemo(() => picked.map((p) => {
    const m = p.meta;
    if (!m) return null;
    if (m.latitude != null && m.longitude != null) {
      return { latitude: m.latitude, longitude: m.longitude, source: 'photo' as const, accuracyM: undefined };
    }
    if (device && deviceFixApplies(m.takenAt)) {
      return { latitude: device.latitude, longitude: device.longitude, source: 'device' as const, accuracyM: device.accuracyM };
    }
    return null;
  }), [picked, device]);

  // #522: nudge when these look like another job's photos.
  const farthest = useMemo(() => {
    if (!site) return null;
    const ds = located.filter((l): l is NonNullable<typeof l> => !!l).map((l) => distanceMeters(site, l));
    return ds.length ? Math.max(...ds) : null;
  }, [located, site]);

  const maxDate = localYmd(new Date(Date.now() + 24 * 3600_000));
  // Checked BEFORE anything uploads (review of #560): a cleared or future day
  // would otherwise send every photo to the Job Folder and only then be
  // refused by the API. <input type="date"> yields '' or a real YYYY-MM-DD.
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(photoDate) && photoDate <= maxDate;

  const remove = (file: File) => {
    // Same warning Cancel gives (review of #560): after a partial failure the
    // uploaded photos are still removable, and removing one drops the only
    // record of a file that is already in the Job Folder.
    if (uploadedRef.current.has(file) && !window.confirm(
      'This photo is already in the Job Folder. Remove it from this post anyway? The file will stay in the folder but won’t be posted.',
    )) return;
    uploadedRef.current.delete(file);
    setFailedCount(0); // the button goes back to "Post N"; post() still skips what's uploaded
    setPicked((prev) => {
      const gone = prev.find((p) => p.file === file);
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((p) => p.file !== file);
    });
  };

  /** Record the post from whatever has uploaded, in the order picked — or,
   *  after a first attempt, resend exactly what that attempt sent. */
  const createPost = async () => {
    if (!attemptRef.current) {
      const photos = picked.map((p) => uploadedRef.current.get(p.file)).filter((x): x is SitePhotoInput => !!x);
      if (photos.length === 0) return;
      attemptRef.current = { photoDate, caption: caption.trim() || undefined, photos };
      setFrozen(true);
    }
    const attempt = attemptRef.current;
    setPosting(true);
    setError(null);
    try {
      const created = await sitePhotosApi.create({ projectId, clientRequestId: requestIdRef.current, ...attempt });
      uploadedRef.current.clear();
      onPosted(created);
      onClose();
    } catch (e) {
      if (isConflictError(e)) {
        // Retrying can only repeat the 409, so don't offer it.
        setFinalError(true);
        setError(`${sentence(e, 'This can’t be posted.')} These photos weren’t posted. Close this and check the job; the files stay in the Job Folder.`);
        setPosting(false);
        return;
      }
      if (isDefiniteRejection(e)) {
        // The API refused THIS request, so nothing was saved: unfreeze, so it
        // can be corrected, and use a new id so the corrected post is a new
        // request. Only an ambiguous failure (no reply, timeout, 5xx) or a 409
        // stays frozen. The uploads are kept either way.
        attemptRef.current = null;
        setFrozen(false);
        requestIdRef.current = randomId();
        setError(`${sentence(e, 'Posting was refused.')} Nothing was posted — fix it and post again; your photos are still uploaded.`);
        setPosting(false);
        return;
      }
      // Pressing the button again resends exactly this attempt.
      const leftOut = picked.length - attempt.photos.length;
      setError(
        `${sentence(e, 'Posting failed.')} Your photos are uploaded — try again to finish posting.`
        + (leftOut > 0
          ? ` This post has ${attempt.photos.length} photo${attempt.photos.length === 1 ? '' : 's'}; add the other ${leftOut} as a new post once it goes through.`
          : ''),
      );
      setPosting(false);
    }
  };

  const post = async () => {
    if (picked.length === 0 || finalError) return;
    // A post was already attempted: only ever resend that same attempt.
    if (attemptRef.current) { await createPost(); return; }
    setPosting(true);
    setError(null);
    setFailedCount(0);
    const todo = picked.map((p, i) => ({ p, i })).filter(({ p }) => !uploadedRef.current.has(p.file));
    // Sign in to SharePoint FIRST, straight from the tap, before any
    // compressing: a sign-in popup opened after seconds of other work is the
    // kind phones block (review of #560). Later uploads reuse the token.
    if (todo.length > 0) {
      try {
        await getToken();
      } catch {
        setPosting(false);
        setError(SIGN_IN_FAILED);
        return;
      }
    }
    setProgress({ done: 0, total: todo.length });
    let failed = 0;
    let consent = false;
    for (let k = 0; k < todo.length; k++) {
      const { p, i } = todo[k];
      try {
        const c = await compressImageSized(p.file, SITE_PHOTO_MAX_DIM);
        let stamp = nameRef.current.get(p.file);
        if (!stamp) {
          const ext = uploadExtension(p.file, c.reencoded);
          const tag = randomId().slice(0, 8);
          stamp = `${jobNumber}_SitePhoto_${photoDate}_${Date.now()}_${i}_${tag}.${ext}`
            .replace(/[:/\\*?"<>|]/g, '').replace(/\s+/g, '');
          nameRef.current.set(p.file, stamp);
        }
        const res = await uploadToJobFolder({
          getToken, jobFolderUrl, subfolder: SUBFOLDER, fileName: stamp, file: c.file, conflictBehavior: 'replace',
        });
        const loc = located[i];
        uploadedRef.current.set(p.file, {
          fileName: res.name,
          webUrl: res.webUrl,
          sizeBytes: res.size,
          width: c.width ?? undefined,
          height: c.height ?? undefined,
          takenAt: p.meta?.takenAt?.toISOString(),
          latitude: loc ? Number(loc.latitude.toFixed(6)) : undefined,
          longitude: loc ? Number(loc.longitude.toFixed(6)) : undefined,
          locationSource: loc?.source,
          locationAccuracyM: loc?.accuracyM,
        });
      } catch (e) {
        failed++;
        if (e instanceof GraphConsentError) { consent = true; break; }
      }
      setProgress({ done: k + 1, total: todo.length });
    }
    setProgress(null);

    if (consent) {
      setPosting(false);
      setError(SIGN_IN_FAILED);
      return;
    }
    if (failed > 0) {
      // Don't post a partial batch silently — let them retry the rest, or
      // choose to post what made it.
      setPosting(false);
      setFailedCount(failed);
      setError(`${failed} of ${picked.length} photos didn’t upload. Check your connection and retry — the ones that uploaded won’t be sent again.`);
      return;
    }
    await createPost();
  };

  const uploadedCount = picked.filter((p) => uploadedRef.current.has(p.file)).length;
  const cancel = () => {
    if (uploadedCount > 0 && !window.confirm(
      `${uploadedCount} photo${uploadedCount === 1 ? ' is' : 's are'} already in the Job Folder. Cancel anyway? They'll stay in the folder but won't be posted.`,
    )) return;
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={posting ? () => undefined : cancel}
      closeOnBackdropClick={!posting}
      title="Post site photos"
      maxWidth={640}
      footer={(
        <>
          <Button variant="secondary" onClick={cancel} disabled={posting}>Cancel</Button>
          {failedCount > 0 && uploadedCount > 0 && !posting && !frozen ? (
            <Button variant="secondary" onClick={createPost}>Post the {uploadedCount} that uploaded</Button>
          ) : null}
          <Button variant="primary" onClick={post} isLoading={posting} disabled={finalError || picked.length === 0 || !allRead || waitingForLocation || (!frozen && !dateValid)}>
            {posting && progress
              ? `Uploading ${progress.done}/${progress.total}…`
              : finalError
                ? 'Can’t be posted'
              : frozen
                ? 'Try posting again'
              : waitingForLocation
                ? 'Getting your location…'
              : failedCount > 0
                ? `Retry ${failedCount}`
                : `Post ${picked.length} photo${picked.length === 1 ? '' : 's'}`}
          </Button>
        </>
      )}
    >
      <style>{css}</style>
      <div className="spc-body">
        <div className="spc-grid">
          {picked.map((p, i) => {
            const loc = located[i];
            return (
              <div key={p.url} className="spc-tile">
                <div className="spc-thumb">
                  <img src={p.url} alt="" />
                  {!posting && !frozen ? (
                    <button type="button" className="spc-remove" onClick={() => remove(p.file)} aria-label="Remove photo">×</button>
                  ) : null}
                </div>
                <div className="spc-meta">
                  {p.meta === null ? 'Reading…' : (
                    <>
                      {p.meta.takenAt ? fmtClock(p.meta.takenAt) : 'No capture time'}
                      <br />
                      {loc
                        ? (site ? describeDistance(distanceMeters(site, loc)) : (loc.source === 'photo' ? 'Location found' : 'Your location'))
                          + (loc.source === 'device' ? ' (phone)' : '')
                        : (locating ? 'Locating…' : 'No location')}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {leftOut > 0 ? (
          <div className="spc-warn">
            A post can hold up to {MAX_PHOTOS} photos, so the last {leftOut} you picked {leftOut === 1 ? 'isn’t' : 'aren’t'} included.
            Post these, then add the rest as another post.
          </div>
        ) : null}

        {farthest != null && farthest > FAR_FROM_SITE_METERS ? (
          <div className="spc-warn">
            Some of these photos were taken about {describeDistance(farthest).replace(' from site', '')} from this job’s address.
            Make sure you’re posting them to the right job.
          </div>
        ) : null}

        <div className="spc-field">
          <label className="spc-label" htmlFor="spc-caption">Caption (optional)</label>
          <textarea
            id="spc-caption"
            className="spc-input"
            rows={2}
            maxLength={1000}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. East footing formed and ready for inspection"
            disabled={posting || frozen}
          />
        </div>

        <div className="spc-field">
          <label className="spc-label" htmlFor="spc-date">Site day</label>
          <input
            id="spc-date"
            className="spc-input"
            type="date"
            value={photoDate}
            max={maxDate}
            onChange={(e) => { setPhotoDate(e.target.value); setDateTouched(true); }}
            disabled={posting || frozen || !!fixedDate}
          />
          {!dateValid && !frozen ? (
            <p className="spc-note" style={{ color: 'var(--ew-danger)' }}>Pick the day these photos are from (today at the latest).</p>
          ) : null}
          <p className="spc-note">
            {fixedDate
              ? 'These photos go on this daily log’s day.'
              : 'Defaults to the day the photos were taken. They also show on that day’s daily log.'}
          </p>
        </div>

        {error ? <div className="spc-error">{error}</div> : null}
      </div>
    </Modal>
  );
}
